import { useEffect, useMemo, useState } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import { Lighting } from "./three/Lighting";
import { Podium, usePodium } from "./three/Podium";
import { PlayerCharacter } from "./three/PlayerCharacter";
import {
  avatarFileForPlayer,
  PODIUM_SCALE,
  PODIUM_CAM_HEIGHT,
  PODIUM_CAM_LOOK_HEIGHT,
  PODIUM_CAM_MIN_DISTANCE,
  PODIUM_CAM_WIDTH_MARGIN,
  PODIUM_CAM_FAR,
} from "./three/constants";
import { Player } from "../types";

/*
 * Scène de test isolée pour regarder le podium de fin de partie sans avoir à
 * jouer une partie entière (admin → set → lobby → répondre à toutes les questions).
 *
 * Accès : /podium-test
 *   - Souris : orbite / zoom / pan (OrbitControls)
 *   - Slider : nombre de joueurs posés sur les places
 *   - Toggle : cadrage caméra du jeu vs orbite libre
 *
 * Le podium est rendu à l'origine ici (et non à PODIUM_STAGE_X) : cette page ne
 * contient ni piste ni décor, il n'y a rien à fuir.
 */

// Avatars fictifs. Les ids sont fixes pour que chaque place garde le même animal
// d'un rechargement à l'autre (avatarFileForPlayer hache l'id).
const FAKE_NAMES = [
  "Sakura", "Momo", "Yuki", "Kenji", "Hana",
  "Riku", "Aoi", "Sora", "Nao", "Rin",
];

function fakePlayers(count: number): Player[] {
  return Array.from({ length: count }, (_, i) => ({
    id: `podium-test-player-${i + 1}`,
    name: FAKE_NAMES[i % FAKE_NAMES.length],
    score: (count - i) * 1000,
    position: 0,
    ready: true,
  })) as Player[];
}

// Applique reellement le cadrage du jeu. OrbitControls.position0 ne sert qu'au
// reset (touche R des controls), il ne bouge pas la camera au montage.
function ApplyFraming({
  enabled,
  pos,
  look,
}: {
  enabled: boolean;
  pos: readonly [number, number, number];
  look: readonly [number, number, number];
}) {
  const { camera } = useThree();
  useEffect(() => {
    if (!enabled) return;
    camera.position.set(pos[0], pos[1], pos[2]);
    camera.lookAt(look[0], look[1], look[2]);
    camera.updateProjectionMatrix();
  }, [enabled, camera, pos, look]);
  return null;
}

export function PodiumTest() {
  const podium = usePodium();
  const [playerCount, setPlayerCount] = useState(3);
  const [gameFraming, setGameFraming] = useState(true);

  const players = useMemo(() => fakePlayers(playerCount), [playerCount]);

  // Rang N -> Place_N, comme en jeu.
  const targets = useMemo(() => {
    const map = new Map<string, [number, number, number]>();
    if (!podium) return map;
    players.forEach((p, i) => {
      const offset = podium.spots.get(i + 1);
      if (!offset) return;
      map.set(p.id, [offset.x, offset.y, offset.z]);
    });
    return map;
  }, [podium, players]);

  const spotList = useMemo(
    () => (podium ? [...podium.spots.entries()].sort((a, b) => a[0] - b[0]) : []),
    [podium]
  );

  // Cadrage identique au repli de SideCamera : centré sur les places occupées.
  const framing = useMemo(() => {
    const used = [...targets.values()];
    const xs = used.length ? used.map((p) => p[0]) : spotList.map(([, v]) => v.x);
    const zs = used.length ? used.map((p) => p[2]) : spotList.map(([, v]) => v.z);
    if (!xs.length) return { pos: [0, 5, 20] as const, look: [0, 1.4, 0] as const };
    const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
    const frontZ = Math.max(...zs);
    const width = Math.max(...xs) - Math.min(...xs);
    const distance = Math.max(
      PODIUM_CAM_MIN_DISTANCE,
      (width * PODIUM_CAM_WIDTH_MARGIN) / 2 / Math.tan((50 * Math.PI) / 180 / 2)
    );
    return {
      pos: [centerX, PODIUM_CAM_HEIGHT, frontZ + distance] as const,
      look: [centerX, PODIUM_CAM_LOOK_HEIGHT, frontZ] as const,
    };
  }, [targets, spotList]);

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-sky-300 to-sky-100 font-kawaii">
      <Canvas shadows // far identique a ce que SideCamera applique en phase podium : la preview
        // doit montrer exactement ce que verra le joueur, clipping compris.
        camera={{ fov: 50, near: 0.1, far: PODIUM_CAM_FAR, position: [0, 6, 26] }}>
        <Lighting />
        {podium && <Podium data={podium} position={[0, 0, 0]} />}

        {podium &&
          players.map((p, i) => (
            <PlayerCharacter
              key={p.id}
              player={p}
              laneIndex={i}
              totalLanes={players.length}
              rank={i + 1}
              isCurrentPlayer={false}
              avatarFile={avatarFileForPlayer(p.id)}
              isWinner={i === 0}
              isFinished
              showLabel
              podiumTarget={targets.get(p.id) ?? null}
              onPodium
            />
          ))}

        <ApplyFraming enabled={gameFraming} pos={framing.pos} look={framing.look} />
        <OrbitControls
          makeDefault
          target={new THREE.Vector3(...framing.look)}
          maxDistance={600}
        />
      </Canvas>

      {/* --- Panneau de contrôle --- */}
      <div className="absolute top-4 right-4 w-72 bg-white/90 backdrop-blur-xl rounded-kawaii shadow-kawaii-lg border-2 border-sakura-200/50 p-4 space-y-3 text-sm">
        <p className="font-black text-sakura-500 text-base">Podium preview</p>

        {!podium ? (
          <p className="font-bold text-amber-600">
            GLB non chargé. Regarde la console : fichier absent, ou Draco/extension
            non supportée.
          </p>
        ) : (
          <>
            <p className="font-bold text-gray-500">
              {spotList.length} places trouvées
              {podium.view ? " · point de vue du GLB utilisé" : " · pas de caméra dans le GLB"}
            </p>

            <label className="block">
              <span className="font-bold text-lavender-500">
                Joueurs sur le podium : {playerCount}
              </span>
              <input
                type="range"
                min={1}
                max={Math.max(1, spotList.length)}
                value={playerCount}
                onChange={(e) => setPlayerCount(Number(e.target.value))}
                className="w-full accent-sakura-400"
              />
            </label>


            <label className="flex items-center gap-2 font-bold text-lavender-500">
              <input
                type="checkbox"
                checked={gameFraming}
                onChange={(e) => setGameFraming(e.target.checked)}
                className="accent-sakura-400"
              />
              Cadrage du jeu
            </label>

            <div className="pt-2 border-t border-lavender-100 text-xs font-bold text-gray-400 space-y-0.5">
              <p>PODIUM_SCALE = {PODIUM_SCALE}</p>
              {spotList.length > 0 && (
                <p>
                  étalement X ={" "}
                  {(
                    Math.max(...spotList.map(([, v]) => v.x)) -
                    Math.min(...spotList.map(([, v]) => v.x))
                  ).toFixed(1)}{" "}
                  unités
                </p>
              )}
              <p className="pt-1">Souris : orbite / molette / clic droit = pan</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
